import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO, { attorneyJsonLd } from "@/components/SEO";
import { Eyebrow, PrimaryCTA } from "@/components/ui-bits";
import { getAttorney } from "@/data/attorneys";
import { FIRM } from "@/data/firm";
import { ArrowLeft } from "lucide-react";

export default function AttorneyBio() {
  const { slug } = useParams();
  const attorney = getAttorney(slug);
  if (!attorney) return <Navigate to="/people" replace />;

  const emailHref = `mailto:${attorney.email}`;

  return (
    <Layout>
      <SEO
        title={attorney.name}
        description={attorney.bio}
        path={`/people/${attorney.slug}`}
        jsonLd={attorneyJsonLd(attorney)}
      />

      <section className="border-b border-hairline" data-testid="attorney-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-14">
          <Link
            to="/people"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-soft hover:text-brass mb-10"
            data-testid="back-to-people"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> People
          </Link>
          <Eyebrow>{attorney.role}</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">{attorney.name}</h1>
          <p className="mt-6 font-serif italic text-2xl text-ink-soft leading-snug max-w-3xl">
            {attorney.role} of {FIRM.longName}.
          </p>
        </div>
      </section>

      <section className="border-b border-hairline" data-testid="attorney-body">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Portrait + quick facts */}
          <aside className="lg:col-span-4 space-y-8">
            <img
              src={attorney.photo}
              alt={attorney.name}
              className="w-full h-auto grayscale border border-hairline"
            />
            <QuickFacts
              label="Practices"
              testId="attorney-practices"
              items={attorney.practices}
              inline
            />
            <QuickFacts
              label="Admissions"
              testId="attorney-admissions"
              items={attorney.admissions}
            />
            <div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-brass-deep font-semibold mb-2">Direct</p>
              <a
                href={emailHref}
                className="text-ink hover:text-brass transition-colors break-all text-sm"
                data-testid="attorney-bio-email"
              >
                {attorney.email}
              </a>
            </div>
          </aside>

          {/* Full body */}
          <div className="lg:col-span-8 prose-law max-w-3xl">
            {attorney.bioLong.map((p, i) => <p key={i}>{p}</p>)}

            {attorney.education?.length > 0 && (
              <>
                <h2>Education</h2>
                <ul>
                  {attorney.education.map((e, i) => (
                    <li key={i}>
                      {e.degree}, {e.school}
                      {e.year ? `, ${e.year}` : ""}
                      {e.honors ? <> <em>({e.honors})</em></> : null}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(attorney.memberships?.current?.length > 0 || attorney.memberships?.prior?.length > 0) && (
              <>
                <h2>Roles &amp; Affiliations</h2>
                {attorney.memberships.current?.length > 0 && (
                  <>
                    <h3 style={{ marginTop: "0.5rem" }}>Current</h3>
                    <ul>
                      {attorney.memberships.current.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </>
                )}
                {attorney.memberships.prior?.length > 0 && (
                  <>
                    <h3>Prior</h3>
                    <ul>
                      {attorney.memberships.prior.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </>
                )}
              </>
            )}

            {attorney.engagements?.length > 0 && (
              <>
                <h2>Speaking &amp; Presentations</h2>
                <p className="text-ink-soft text-sm italic mb-4" style={{ marginTop: "-0.25rem" }}>
                  Selected engagements, most recent first.
                </p>
                <ul>
                  {attorney.engagements.map((e, i) => (
                    <li key={i}>
                      <em>{e.title}</em>
                      {e.venue ? <> — {e.venue}</> : null}
                      {e.date ? <> ({e.date})</> : null}
                      {e.note ? <>. {e.note}</> : "."}
                      {e.links?.length > 0 && (
                        <>
                          {" "}
                          {e.links.map((l, j) => (
                            <span key={j}>
                              {j > 0 ? " · " : ""}
                              <a href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
                            </span>
                          ))}
                          .
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <PrimaryCTA to="/contact" testId="attorney-bio-cta">Contact the Firm</PrimaryCTA>
              <a href={emailHref} className="text-sm text-ink-soft hover:text-brass transition-colors" data-testid="attorney-bio-direct">
                <span className="text-[10px] uppercase tracking-[0.24em] text-brass-deep font-semibold mr-2">Direct</span>
                {attorney.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function QuickFacts({ label, items, inline = false, testId }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.24em] text-brass-deep font-semibold mb-2">{label}</p>
      {inline ? (
        <p className="text-ink text-sm leading-relaxed">{items.join(" · ")}</p>
      ) : (
        <ul className="text-ink text-sm leading-relaxed space-y-0.5">
          {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}
